import React, { useContext, useMemo } from 'react'
import { AuditContext } from '../../../../../context/AuditContext'
import { useAudits } from '../../../../async/model/audit/useAudit'
import { FilterName, FilterParam } from '../FilterBar.types'
import { FilterDropdown } from './FilterDropdown'
import { DropdownItemType } from './FilterDropdown.constants'

export const AUDIT_FILTER_NONE_VALUE = 'none'

// A defaultSelection of just the "no audit" option.
export const AUDIT_DEFAULT_NONE = [AUDIT_FILTER_NONE_VALUE]

const NO_DEFAULT_SELECTION: string[] = []

export const AuditFilterDropdown = React.memo(
  ({
    isNested = false,
    includeNone = false,
    noneLabel = 'None',
    includeClosed = false,
    defaultSelection = NO_DEFAULT_SELECTION,
  }: {
    isNested?: boolean
    includeNone?: boolean
    // Label for the "no audit" option.
    noneLabel?: string
    // List closed audits too, e.g. where history in a closed audit is still useful.
    includeClosed?: boolean
    // What counts as selected when the URL has no audit filter. The page applying the
    // filter must use the same default.
    defaultSelection?: string[]
  }): JSX.Element => {
    const { auditId } = useContext(AuditContext)
    const { data, error, isLoading } = useAudits()
    const filterValues = useMemo<DropdownItemType[]>(
      () => [
        ...(includeNone
          ? [{ value: AUDIT_FILTER_NONE_VALUE, label: noneLabel }]
          : []),
        ...(data?.getItemsList() ?? [])
          .filter((audit) => includeClosed || !audit.getIsClosed())
          .map((audit) => ({ value: audit.getId(), label: audit.getName() })),
      ],
      [data, includeNone, noneLabel, includeClosed],
    )
    // In an audit, the audit is already chosen.
    if (error || auditId) return <></>

    return (
      <FilterDropdown
        filterParam={FilterParam.AUDIT}
        filterName={FilterName.AUDIT}
        filterValues={filterValues}
        isLoading={!data || isLoading}
        isNested={isNested}
        defaultSelection={defaultSelection}
      />
    )
  },
)
